import React, { useEffect, useRef, useState } from 'react';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyIcon from '@mui/icons-material/Key';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { appBarHeightAtom } from '../atoms/AppHeader';
import { selectedPageAtom } from '../atoms/Page';
import { sharedStyles } from '../utils/Style';
import { tokenDataAtom } from '../atoms/TokenData';
import { getAvailablePages } from '../variables/Page';
import ChangePasswordDialog from './User/ChangePasswordDialog';
import { clearAuthToken } from '../utils/Auth';
import { Page } from '../interfaces/Page';

const DummyScrollButton = () => <></>;

const AppHeader = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('lg'));
  const [navigationOpen, setNavigationOpen] = useState(false);

  useEffect(() => {
    setNavigationOpen(false);
  }, [location.pathname, isCompact]);

  const ref = useRef<HTMLDivElement>(null);

  const setAppBarHeight = useSetRecoilState(appBarHeightAtom);
  const [selectedPage, setSelectedPage] = useRecoilState(selectedPageAtom);
  const tokenData = useRecoilValue(tokenDataAtom);

  const [settingsPopoverAnchor, setSettingsPopoverAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [showChangePasswordDialog, setShowChangePasswordDialog] =
    useState(false);

  const showSettingsPopover = Boolean(settingsPopoverAnchor);
  const settingsPopoverId = showSettingsPopover
    ? 'settings-popover'
    : undefined;

  useEffect(() => {
    const header = ref.current;
    if (!header) return undefined;

    const updateHeight = () => setAppBarHeight(header.clientHeight);
    updateHeight();
    const observer = new ResizeObserver(updateHeight);
    observer.observe(header, { box: 'border-box' });
    return () => observer.disconnect();
  }, [setAppBarHeight]);

  const pages = getAvailablePages(tokenData).filter((page) => !page.disabled);

  const handleNavigateMainPage = () => {
    navigate('/');
  };

  const handleNavigate = (page: Page) => {
    setSelectedPage(page.pageType);
    navigate(page.path);
    setNavigationOpen(false);
  };

  const handleTabChange = (
    event: React.ChangeEvent<{}>,
    newValue: Page['pageType'],
  ) => {
    const page = pages.find((item) => item.pageType === newValue);
    if (page) handleNavigate(page);
  };

  const handleShowSettingsPopover = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    setSettingsPopoverAnchor(event.currentTarget);
  };

  const handleCloseSettingsPopover = () => {
    setSettingsPopoverAnchor(null);
  };

  const handleLogout = () => {
    clearAuthToken();
    navigate('/login');
  };

  return (
    <Box component='div'>
      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onClose={() => setShowChangePasswordDialog(false)}
      />
      <AppBar
        position='fixed'
        elevation={1}
        ref={ref}
        sx={{
          backgroundColor: '#FFFFFF',
          paddingTop: 'var(--app-safe-area-top)',
          paddingLeft: 'var(--app-safe-area-left)',
          paddingRight: 'var(--app-safe-area-right)',
          color: 'text.primary',
        }}
      >
        <Box
          component='div'
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexGrow: 1,
            alignItems: { xs: 'center', lg: 'stretch' },
            minHeight: { xs: 56, lg: 0 },
            paddingRight: { xs: 1, lg: 3 },
            paddingLeft: 1,
          }}
        >
          {isCompact && (
            <IconButton
              color='primary'
              aria-label={t('app_header.open_navigation') || undefined}
              aria-controls={navigationOpen ? 'mobile-navigation' : undefined}
              aria-expanded={navigationOpen}
              onClick={() => setNavigationOpen(true)}
              sx={{ width: 48, height: 48 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Button
            onClick={handleNavigateMainPage}
            color='primary'
            sx={{
              ...sharedStyles.buttonText,
              ...sharedStyles.h6,
              p: 1.5,
              minHeight: 0,
              mt: { xs: 0, lg: 'auto' },
              mb: { xs: 0, lg: 0.5 },
              mr: 1,
            }}
          >
            <Box component='img' src='/nobetio.png' alt='nöbetio' width={90} />
          </Button>
          <Box
            component='div'
            sx={{
              display: { xs: 'none', lg: 'flex' },
              flexGrow: 1,
              minWidth: 0,
              marginBottom: 0,
              height: '100%',
            }}
          >
            <Tabs
              variant='scrollable'
              value={
                pages.some((page) => page.pageType === selectedPage)
                  ? selectedPage
                  : false
              }
              onChange={handleTabChange}
              ScrollButtonComponent={DummyScrollButton}
            >
              {pages.map((page) => (
                <Tab
                  key={page.pageType}
                  value={page.pageType}
                  label={
                    <Typography sx={sharedStyles.tabLabel}>
                      {t(page.name)}
                    </Typography>
                  }
                />
              ))}
            </Tabs>
          </Box>
          {isCompact ? (
            <IconButton
              color='primary'
              aria-label={t('app_header.account') || undefined}
              aria-controls={settingsPopoverId}
              aria-expanded={showSettingsPopover}
              onClick={handleShowSettingsPopover}
              sx={{ ml: 'auto', width: 48, height: 48 }}
            >
              <AccountCircleIcon />
            </IconButton>
          ) : (
            <Button
              startIcon={<AccountCircleIcon fontSize='large' />}
              size='large'
              onClick={handleShowSettingsPopover}
              sx={{ ...sharedStyles.buttonText, fontSize: 16 }}
            >
              {tokenData.username || t('app_header.guest')}
            </Button>
          )}
          <Popover
            id={settingsPopoverId}
            open={showSettingsPopover}
            anchorEl={settingsPopoverAnchor}
            onClose={handleCloseSettingsPopover}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <Box
              component='div'
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <Button
                startIcon={<KeyIcon />}
                disabled={false}
                onClick={() => {
                  handleCloseSettingsPopover();
                  setShowChangePasswordDialog(true);
                }}
                sx={{
                  padding: 1.5,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                }}
              >
                {t('user.change_password')}
              </Button>
              <Button
                startIcon={<LogoutIcon />}
                disabled={false}
                onClick={handleLogout}
                sx={{
                  padding: 1.5,
                  textTransform: 'none',
                  justifyContent: 'flex-start',
                }}
              >
                {t('app_header.settings.sign_out')}
              </Button>
            </Box>
          </Popover>
        </Box>
      </AppBar>
      <Drawer
        anchor='left'
        open={isCompact && navigationOpen}
        onClose={() => setNavigationOpen(false)}
        PaperProps={{
          sx: {
            width: 300,
            maxWidth: '85vw',
            boxSizing: 'border-box',
            pt: 'var(--app-safe-area-top)',
            pb: 'var(--app-safe-area-bottom)',
            pl: 'var(--app-safe-area-left)',
          },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', px: 2, minHeight: 64 }}
        >
          <Box component='img' src='/nobetio.png' alt='nöbetio' width={90} />
          <IconButton
            aria-label={t('app_header.close_navigation') || undefined}
            onClick={() => setNavigationOpen(false)}
            sx={{ ml: 'auto', width: 48, height: 48 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        <Box
          component='nav'
          id='mobile-navigation'
          aria-label={t('app_header.navigation') || undefined}
        >
          <List sx={{ px: 1 }}>
            {pages.map((page) => (
              <ListItemButton
                key={page.pageType}
                selected={page.pageType === selectedPage}
                aria-current={
                  page.pageType === selectedPage ? 'page' : undefined
                }
                onClick={() => handleNavigate(page)}
                sx={{ minHeight: 48, borderRadius: 1 }}
              >
                <ListItemText primary={t(page.name)} />
              </ListItemButton>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
};

export default AppHeader;
